import React from "react";
import { Chip, FormControl, FormControlLabel, Grid, RadioGroup, Stack, Switch, Typography } from "@mui/material";
import { LocalOffer as LocalOfferIcon } from "@mui/icons-material";

import AppDatePicker from "../../shared/AppDatePicker";
import { PLAN_DEFINITIONS, PLAN_ORDER, PLAN_TIERS, BILLING_CYCLES, formatCurrency, monthlyPriceForTier } from "../../../subscription";
import { PlanCard, TierCard } from "./RegistrationPlanCards";

export default function RegistrationPlanSection({
    showInclVat,
    onToggleVat,
    adminRegistrationAvailable,
    registerAsAdmin,
    onAdminToggle,
    tier,
    showTestPlanOption,
    basePlanFeatures,
    accountingFeatures,
    onTierSelect,
    isTestPlan,
    paidUntil,
    paidUntilError,
    onPaidUntilChange,
    cycle,
    onCycleSelect,
    monthlyPrice,
    yearlyPrice,
    vatLabel,
    yearlySavingDisplay,
    yearlyEffective,
}) {
    return (
        <>
            <Grid item xs={12}>
                <FormControlLabel
                    control={<Switch checked={showInclVat} onChange={(event) => onToggleVat(event.target.checked)} />}
                    label="Preise inkl. USt (19 %) anzeigen"
                />
            </Grid>

            {adminRegistrationAvailable && (
                <Grid item xs={12}>
                    <Stack
                        spacing={0.5}
                        sx={{
                            p: 1.5,
                            borderRadius: 2,
                            border: (theme) => `1px solid ${theme.palette.divider}`,
                        }}
                    >
                        <FormControlLabel
                            control={<Switch checked={registerAsAdmin} onChange={onAdminToggle} />}
                            label="Admin-User einmalig anlegen"
                        />
                        <Typography variant="caption" color="text.secondary">
                            Verfügbar, solange noch kein Admin existiert.
                        </Typography>
                    </Stack>
                </Grid>
            )}

            <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Modul auswählen
                </Typography>
                <FormControl fullWidth>
                    <RadioGroup
                        row
                        name="planTier"
                        value={tier}
                        onChange={(event) => onTierSelect(event.target.value)}
                        sx={{ gap: 2, flexWrap: "wrap" }}
                    >
                        {(registerAsAdmin ? [] : PLAN_ORDER).map((planTier) => {
                            const def = PLAN_DEFINITIONS[planTier];
                            const featuresForCard =
                                planTier === PLAN_TIERS.ACCOUNTING
                                    ? [...basePlanFeatures, ...PLAN_DEFINITIONS[PLAN_TIERS.ACCOUNTING].features]
                                    : basePlanFeatures;
                            return (
                                <TierCard
                                    key={planTier}
                                    value={planTier}
                                    selected={tier === planTier}
                                    title={def.title}
                                    price={`${formatCurrency(monthlyPriceForTier(planTier, showInclVat))} / Monat`}
                                    subtitle={def.subtitle}
                                    features={featuresForCard}
                                    highlight={planTier === PLAN_TIERS.ACCOUNTING}
                                />
                            );
                        })}
                        {showTestPlanOption && (
                            <TierCard
                                value="TEST"
                                selected={tier === "TEST"}
                                title={PLAN_DEFINITIONS.TEST.title}
                                price="0,00 €"
                                subtitle={PLAN_DEFINITIONS.TEST.subtitle}
                                features={accountingFeatures}
                                highlight={false}
                            />
                        )}
                    </RadioGroup>
                </FormControl>
            </Grid>

            {isTestPlan && (
                <Grid item xs={12}>
                    <AppDatePicker
                        label="Test-Zugang gültig bis"
                        name="paidUntil"
                        value={paidUntil}
                        onChange={onPaidUntilChange}
                        fullWidth
                        required
                        error={Boolean(paidUntilError)}
                        helperText={paidUntilError || "Wähle das Enddatum für diesen Test-Account."}
                    />
                </Grid>
            )}

            {!isTestPlan && (
                <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Zahlungsrhythmus
                    </Typography>
                    <FormControl fullWidth>
                        <RadioGroup
                            row
                            name="paymentPlan"
                            value={cycle}
                            onChange={(event) => onCycleSelect(event.target.value)}
                            sx={{ gap: 2, flexWrap: "wrap" }}
                        >
                            <PlanCard
                                value={BILLING_CYCLES.MONTHLY}
                                selected={cycle === BILLING_CYCLES.MONTHLY}
                                title="Monatlich"
                                price={`${formatCurrency(monthlyPrice)} / Monat`}
                                badge={null}
                                vatLabel={vatLabel}
                            />
                            <PlanCard
                                value={BILLING_CYCLES.YEARLY}
                                selected={cycle === BILLING_CYCLES.YEARLY}
                                title="Jährlich"
                                price={`${formatCurrency(yearlyPrice)} / Jahr`}
                                badge={
                                    yearlySavingDisplay ? (
                                        <Chip
                                            icon={<LocalOfferIcon />}
                                            label={`Spare ${yearlySavingDisplay}`}
                                            size="small"
                                            color="warning"
                                            variant="outlined"
                                        />
                                    ) : null
                                }
                                helper={yearlyEffective ? `Effektiv ${yearlyEffective} / Monat` : ""}
                                vatLabel={vatLabel}
                            />
                        </RadioGroup>
                    </FormControl>
                </Grid>
            )}
        </>
    );
}
